import { useState, useMemo } from "react";
import {
  Box,
  Drawer,
  Toolbar,
  List,
  ListItem,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
// import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Link } from "react-router-dom";
import AddOrderDialog from "../../pages/orders/add-order/add-order";
import BulkOrderDialog from "../../pages/orders/bulk-order/bulk-order";
import Notification from "../../components/notification/notification";
import { useSelector } from "react-redux";
import { userType } from "../../utils/Constant";
import {
  getAggregatedPermissions,
  PermissionKeys,
  hasPermission,
} from "../../utils/permissions";

const drawerWidth = 240;

const styles = {
  btnContent: {
    color: "rgba(0, 0, 0, 0.87)",
    textDecoration: "none",
    width: "100%",
  },
  subListItem: {
    pl: 9,
  },
};

export default function Sidebar() {
  const [openOrders, setOpenOrders] = useState(true);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openNewOrder, setOpenNewOrder] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const currentUser = useSelector((state: any) => state.user.user?.currentUser);

  // Aggregate permissions from all roles
  const userPermissions = useMemo(() => {
    if (!currentUser?.roles) {
      return new Set<string>();
    }
    return getAggregatedPermissions(currentUser.roles);
  }, [currentUser?.roles]);

  // Permission checks for menu items
  // const hasOrdersPermission = hasPermission(
  //   userPermissions,
  //   PermissionKeys.Orders
  // );
  const hasClientsPermission = hasPermission(
    userPermissions,
    PermissionKeys.Clients
  );
  const hasUsersPermission = hasPermission(
    userPermissions,
    PermissionKeys.Users
  );
  const hasRolesPermission = hasPermission(
    userPermissions,
    PermissionKeys.Roles
  );
  const hasInspectorFirmsPermission = hasPermission(
    userPermissions,
    PermissionKeys.InspectorFirms
  );
  const hasInvoicesPermission = hasPermission(
    userPermissions,
    PermissionKeys.Invoices
  );
  const hasFormTemplatesPermission = hasPermission(
    userPermissions,
    PermissionKeys.FormTemplates
  );

  // Show Admin section if user has any admin permission
  const hasAnyAdminPermission =
    hasClientsPermission ||
    hasUsersPermission ||
    hasRolesPermission ||
    hasInspectorFirmsPermission ||
    hasInvoicesPermission ||
    hasFormTemplatesPermission;

  const handleClick = () => {
    setOpenDialog(true);
  };

  const handleBulkClick = () => {
    setOpenBulkDialog(true);
  };

  const handleNewOrderClick = () => {
    setOpenNewOrder(!openNewOrder);
  };

  const handleOrderClick = () => {
    setOpenOrders(!openOrders);
  };

  const handleAdminClick = () => {
    setOpenAdmin(!openAdmin);
  };

  const addOrder = () => {
    setShowAlert(true);
  };

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: "auto" }}>
          <List>
            {/* <Link style={styles.btnContent} to={`/`}>
              <ListItemButton>
                <ListItemIcon>
                  <SpaceDashboardOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </Link> */}
            {currentUser?.userType === userType.tenant && (
              <>
                <ListItemButton onClick={handleOrderClick}>
                  <ListItemIcon>
                    <ContentPasteOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Orders" />
                  {openOrders ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openOrders} timeout="auto" unmountOnExit>
                  <ListItem component="div" disablePadding>
                    <ListItemButton
                      onClick={handleNewOrderClick}
                      sx={{
                        color: "primary.main",
                        pl: 9,
                        fontSize: "14px",
                        fontWeight: 400,
                      }}
                    >
                      <ListItemText primary="+ NEW ORDER" />
                      {openNewOrder ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openNewOrder} timeout="auto" unmountOnExit>
                    <ListItem component="div" disablePadding>
                      <ListItemButton
                        sx={{
                          pl: 12,
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                        onClick={handleClick}
                      >
                        <ListItemText primary="Single Order" />
                      </ListItemButton>
                    </ListItem>
                    <ListItem component="div" disablePadding>
                      <ListItemButton
                        sx={{
                          pl: 12,
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                        onClick={handleBulkClick}
                      >
                        <ListItemText primary="Bulk Order" />
                      </ListItemButton>
                    </ListItem>
                  </Collapse>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/all-orders`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="All Orders" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/created`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Created" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/validation`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Validation" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/assign`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Assign" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/accepted`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Accepted" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/qc-review`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="QC Review" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/billing`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Billing" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/issues`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Issues" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/on-hold`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="On Hold" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/closed`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Closed" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                </Collapse>
              </>
            )}

            {currentUser?.userType === userType.client && (
              <>
                <ListItemButton onClick={handleOrderClick}>
                  <ListItemIcon>
                    <ContentPasteOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Orders" />
                  {openOrders ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openOrders} timeout="auto" unmountOnExit>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/all-orders`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="All Orders" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/created`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Created" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/in-progress`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="In Progress" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/billing`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Billing" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/issues`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Issues" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/on-hold`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="On Hold" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/closed`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Closed" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                </Collapse>
              </>
            )}

            {currentUser?.userType === userType.inspector && (
              <>
                <ListItemButton onClick={handleOrderClick}>
                  <ListItemIcon>
                    <ContentPasteOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Orders" />
                  {openOrders ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openOrders} timeout="auto" unmountOnExit>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/all-orders`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="All Orders" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/assign`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Assign" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/accepted`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Accepted" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/submitted`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Submitted" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/issues`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Issues" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/on-hold`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="On Hold" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                  <ListItem component="div" disablePadding>
                    <Link style={styles.btnContent} to={`/closed`}>
                      <ListItemButton sx={styles.subListItem}>
                        <ListItemText primary="Closed" />
                      </ListItemButton>
                    </Link>
                  </ListItem>
                </Collapse>
              </>
            )}

            {currentUser?.userType === userType?.tenant ? (
              [
                hasAnyAdminPermission && (
                  <>
                    <ListItemButton onClick={handleAdminClick}>
                      <ListItemIcon>
                        <SettingsOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText primary="Admin" />
                      {openAdmin ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={openAdmin} timeout="auto" unmountOnExit>
                      {hasUsersPermission && (
                        <ListItem component="div" disablePadding>
                          <Link style={styles.btnContent} to={`/users`}>
                            <ListItemButton sx={styles.subListItem}>
                              <ListItemText primary="Users" />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                      {hasClientsPermission && (
                        <ListItem component="div" disablePadding>
                          <Link style={styles.btnContent} to={`/clients`}>
                            <ListItemButton sx={styles.subListItem}>
                              <ListItemText primary="Clients" />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                      {hasInspectorFirmsPermission && (
                        <ListItem component="div" disablePadding>
                          <Link
                            style={styles.btnContent}
                            to={`/inspection-firms`}
                          >
                            <ListItemButton sx={styles.subListItem}>
                              <ListItemText primary="Inspection Firms" />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                      {hasInvoicesPermission && (
                        <ListItem component="div" disablePadding>
                          <Link style={styles.btnContent} to={`/invoicing`}>
                            <ListItemButton sx={styles.subListItem}>
                              <ListItemText primary="Invoicing" />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                      {hasFormTemplatesPermission && (
                        <ListItem component="div" disablePadding>
                          <Link
                            style={styles.btnContent}
                            to={`/form-templates`}
                          >
                            <ListItemButton sx={styles.subListItem}>
                              <ListItemText primary="Form Templates" />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                      {hasRolesPermission && (
                        <ListItem component="div" disablePadding>
                          <Link
                            style={styles.btnContent}
                            to={`/roles-permissions`}
                          >
                            <ListItemButton sx={styles.subListItem}>
                              <ListItemText primary="Roles & Permissions" />
                            </ListItemButton>
                          </Link>
                        </ListItem>
                      )}
                    </Collapse>
                  </>
                ),
              ]
            ) : (
              <Link style={styles.btnContent} to={`/my-organization`}>
                <ListItemButton>
                  <ListItemIcon>
                    <SettingsOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Admin" />
                </ListItemButton>
              </Link>
            )}
          </List>
        </Box>
      </Drawer>
      {openDialog && (
        <AddOrderDialog
          showDialog={openDialog}
          closeDialog={() => setOpenDialog(false)}
          addOrder={addOrder}
        />
      )}
      {openBulkDialog && (
        <BulkOrderDialog
          showDialog={openBulkDialog}
          closeDialog={() => setOpenBulkDialog(false)}
          addOrder={addOrder}
        />
      )}
      <Notification
        show={showAlert}
        title="New Order Created Successfully"
        message=""
        onClose={() => setShowAlert(false)}
      />
    </>
  );
}
